import { Injectable, inject } from '@angular/core';
import { ApiDataAccess, ResponseRequestEvent } from '@softline/core';
import { lastValueFrom } from 'rxjs';
import {
  SOFTLINE_API_CLAIM_REPORT_BY_WORKFLOW,
  SOFTLINE_API_CLAIM_REPORT_CHECKPOINTS,
  SOFTLINE_API_CLAIM_REPORT_CREATE,
  SOFTLINE_API_CLAIM_REPORT_BY_ID,
  SOFTLINE_API_CLAIM_REPORT_FINALIZE,
  SOFTLINE_API_CLAIM_REPORT_ITEMS,
  SOFTLINE_API_CLAIM_REPORT_SIGNATURE,
} from '../claim-report.api';
import { ComplaintCreateRequest } from '../types/complaint-create-request';
import {
  ComplaintCheckpoint,
  ComplaintCheckpointCreate,
  ComplaintCheckpointPatch,
  ComplaintItem,
  ComplaintPatchRequest,
  ComplaintResponse,
} from '../types/complaint-response';
import {
  ComplaintSignatureCreateRequest,
  ComplaintSignatureRole,
} from '../types/complaint-signature';

function sortCheckpoints(
  checkpoints: ComplaintCheckpoint[] | null | undefined
): ComplaintCheckpoint[] {
  return [...(checkpoints ?? [])].sort((a, b) => a.position - b.position);
}

@Injectable()
export class ComplaintRepository {
  private readonly dataAccess = inject(ApiDataAccess);

  create(value: ComplaintCreateRequest): Promise<ComplaintResponse> {
    return this.dataAccess.create({ path: SOFTLINE_API_CLAIM_REPORT_CREATE }, value);
  }

  loadByWorkflowId(workflowId: number): Promise<ComplaintResponse> {
    return this.dataAccess.get({
      path: SOFTLINE_API_CLAIM_REPORT_BY_WORKFLOW,
      pathParams: { workflowId },
    });
  }

  patch(id: number, body: ComplaintPatchRequest): Promise<ComplaintResponse> {
    return this.dataAccess.patch<ComplaintPatchRequest, ComplaintResponse>(
      { path: SOFTLINE_API_CLAIM_REPORT_BY_ID, pathParams: { id } },
      body
    );
  }

  async loadItems(id: number): Promise<ComplaintItem[]> {
    const items = await this.dataAccess.get<ComplaintItem[] | null>({
      path: SOFTLINE_API_CLAIM_REPORT_ITEMS,
      pathParams: { id },
    });
    return items ?? [];
  }

  async loadCheckpoints(id: number): Promise<ComplaintCheckpoint[]> {
    const checkpoints = await this.dataAccess.get<ComplaintCheckpoint[] | null>({
      path: SOFTLINE_API_CLAIM_REPORT_CHECKPOINTS,
      pathParams: { id },
    });
    return sortCheckpoints(checkpoints);
  }

  async createCheckpoint(
    id: number,
    body: ComplaintCheckpointCreate
  ): Promise<ComplaintCheckpoint[]> {
    const checkpoints = await this.dataAccess.create<
      ComplaintCheckpointCreate,
      ComplaintCheckpoint[] | null
    >({ path: SOFTLINE_API_CLAIM_REPORT_CHECKPOINTS, pathParams: { id } }, body);
    return sortCheckpoints(checkpoints);
  }

  async patchCheckpoint(
    id: number,
    body: ComplaintCheckpointPatch
  ): Promise<ComplaintCheckpoint[]> {
    const checkpoints = await this.dataAccess.patch<
      ComplaintCheckpointPatch,
      ComplaintCheckpoint[] | null
    >({ path: SOFTLINE_API_CLAIM_REPORT_CHECKPOINTS, pathParams: { id } }, body);
    return sortCheckpoints(checkpoints);
  }

  async deleteCheckpoint(
    id: number,
    checkpointId: number
  ): Promise<ComplaintCheckpoint[]> {
    // the backend deletes ALL checkpoints when checkpointId is missing
    if (!Number.isFinite(checkpointId)) {
      throw new Error('checkpointId is required to delete a checkpoint');
    }
    const checkpoints = await this.dataAccess.delete<ComplaintCheckpoint[] | null>({
      path: SOFTLINE_API_CLAIM_REPORT_CHECKPOINTS,
      pathParams: { id },
      queryParams: { checkpointId },
    });
    return sortCheckpoints(checkpoints);
  }

  async finalize(id: number): Promise<Blob> {
    const event = (await lastValueFrom(
      this.dataAccess.download(
        { path: SOFTLINE_API_CLAIM_REPORT_FINALIZE, pathParams: { id } },
        {},
        { responseType: 'blob' }
      )
    )) as ResponseRequestEvent<Blob>;

    if (!event.response) throw new Error('Claim report service returned no PDF');
    return event.response;
  }

  uploadSignature(
    id: number,
    role: ComplaintSignatureRole,
    request: ComplaintSignatureCreateRequest
  ): Promise<void> {
    return this.dataAccess.create(
      { path: SOFTLINE_API_CLAIM_REPORT_SIGNATURE, pathParams: { id, role } },
      request
    );
  }
}
